import { SiteReportDraft, type SiteReportSnapshot } from '@epc/contracts';
import { type DayPackage, editableReport } from './model';
import {
  type PhotoSyncResult,
  type PhotoUploader,
  photosPending,
  syncSitePhotos,
  withUploadedFileIds,
} from './offline/photo-sync';
import { replacePendingSitePhoto, storePreparedSitePhoto } from './offline/photos';
import {
  enqueueSiteCommand,
  resolveSiteConflict,
  resumeSiteSession,
  type SiteTransport,
  saveSiteDraft,
  syncSiteOnce,
} from './offline/queue';
import {
  type LocalSiteReport,
  type OfflineScope,
  SiteOfflineError,
  type SiteOfflineStore,
} from './offline/store';

export const pendingCommand = (row: LocalSiteReport) =>
  row.commands.find((c) => c.state !== 'sent' && !c.resolvedBy);

/** Очередь операций экрана не позволяет запоздалому ACK затереть новый ввод. */
export class SiteEditor {
  row: LocalSiteReport | null = null;
  private tail: Promise<unknown> = Promise.resolve();
  constructor(
    readonly store: SiteOfflineStore,
    readonly scope: OfflineScope,
    readonly clientId: string,
    readonly transport: SiteTransport,
  ) {}

  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.tail.then(fn);
    this.tail = next.catch(() => {});
    return next;
  }
  load() {
    return this.serial(async () => (this.row = await this.store.read(this.scope, this.clientId)));
  }
  save(draft: unknown) {
    if (
      this.row &&
      pendingCommand(this.row)?.action === 'submit' &&
      pendingCommand(this.row)?.state === 'pending'
    )
      return Promise.reject(
        new SiteOfflineError('submit_pending', 'Дождитесь результата отправки на проверку'),
      );
    return this.serial(async () => {
      if (
        this.row &&
        pendingCommand(this.row)?.action === 'submit' &&
        pendingCommand(this.row)?.state === 'pending'
      )
        throw new SiteOfflineError('submit_pending', 'Дождитесь результата отправки на проверку');
      if (!editableReport(this.row?.serverSnapshot as SiteReportSnapshot | null, this.scope.userId))
        throw new SiteOfflineError('closed', 'Этот отчёт уже закрыт для изменения');
      await saveSiteDraft(
        this.store,
        this.scope,
        this.clientId,
        this.withFileIds(draft),
        this.row?.localRevision ?? 0,
        Date.now(),
      );
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  /** Загруженные в фоне фото уже имеют fileId; правка текста на экране не должна его стирать. */
  private withFileIds(draft: unknown) {
    const parsed = SiteReportDraft.safeParse(draft);
    return parsed.success && this.row ? withUploadedFileIds(parsed.data, this.row.photos) : draft;
  }
  /** Фото с камеры: снимок и его описание в черновике сохраняются одной записью на устройстве. */
  addPhoto(draft: SiteReportDraft, clientPhotoId: string, blob: Blob) {
    return this.serial(async () => {
      if (!this.row) throw new Error('Черновик ещё не загружен');
      if (!editableReport(this.row.serverSnapshot as SiteReportSnapshot | null, this.scope.userId))
        throw new SiteOfflineError('closed', 'Этот отчёт уже закрыт для изменения');
      await storePreparedSitePhoto(
        this.store,
        this.scope,
        this.clientId,
        withUploadedFileIds(draft, this.row.photos),
        clientPhotoId,
        blob,
        this.row.localRevision,
        Date.now(),
      );
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  /** Пометка на ещё не отправленном снимке: новый JPEG и sha256 в черновике. */
  replacePhoto(draft: SiteReportDraft, clientPhotoId: string, blob: Blob) {
    return this.serial(async () => {
      if (!this.row) throw new Error('Черновик ещё не загружен');
      if (!editableReport(this.row.serverSnapshot as SiteReportSnapshot | null, this.scope.userId))
        throw new SiteOfflineError('closed', 'Этот отчёт уже закрыт для изменения');
      await replacePendingSitePhoto(
        this.store,
        this.scope,
        this.clientId,
        withUploadedFileIds(draft, this.row.photos),
        clientPhotoId,
        blob,
        this.row.localRevision,
        Date.now(),
      );
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  /** Фоновая загрузка фото; строка перечитывается, чтобы следующая запись шла от свежей версии. */
  syncPhotos(uploader: PhotoUploader): Promise<PhotoSyncResult & { row: LocalSiteReport }> {
    return this.serial(async () => {
      const result = await syncSitePhotos(this.store, this.scope, this.clientId, uploader, Date.now());
      this.row = await this.store.read(this.scope, this.clientId);
      return { ...result, row: this.row! };
    });
  }
  /** Сколько фото ещё не загружено на сервер. */
  photosWaiting() {
    return this.row ? photosPending(this.row.draft as SiteReportDraft, this.row.photos) : 0;
  }
  /** Свежая серверная версия заменяет только полностью синхронизированный черновик. */
  adoptServer(report: SiteReportSnapshot) {
    return this.serial(async () => {
      const expected = this.row?.localRevision;
      await this.store.mutate(this.scope, this.clientId, (row) => {
        if (!row || row.localRevision !== expected)
          throw new SiteOfflineError('local_conflict', 'Черновик изменён в другой вкладке');
        if (
          report.id !== row.serverId ||
          report.clientId !== this.clientId ||
          report.tenantId !== this.scope.tenantId ||
          report.authorId !== this.scope.userId
        )
          throw new SiteOfflineError('invalid_scope', 'Серверная версия принадлежит другому отчёту');
        if (pendingCommand(row) || row.localRevision !== row.syncedLocalRevision)
          throw new SiteOfflineError('local_changes', 'Сначала подтвердите отправку локальных изменений');
        if (report.revision <= row.revision) return { row, result: undefined };
        row.serverSnapshot = report;
        row.revision = report.revision;
        row.draft = report.draft;
        row.localRevision += 1;
        row.syncedLocalRevision = row.localRevision;
        row.savedAt = Date.now();
        return { row, result: undefined };
      });
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  command(action: 'save' | 'submit', sources: DayPackage['sourceRevisions']) {
    return this.serial(() => this.commandNow(action, sources));
  }
  private async commandNow(action: 'save' | 'submit', sources: DayPackage['sourceRevisions']) {
    {
      const row = this.row;
      if (!row) throw new Error('Черновик ещё не загружен');
      const draft = SiteReportDraft.parse(row.draft);
      if (!editableReport(row.serverSnapshot as SiteReportSnapshot | null, this.scope.userId))
        throw new Error('Отчёт уже передан на проверку');
      if (action === 'submit' && !draft.lines.length)
        throw new Error('Добавьте хотя бы одну выполненную работу');
      if (action === 'submit' && !row.serverId) throw new Error('Сначала сохраните отчёт на сервере');
      const operation = crypto.randomUUID();
      const envelope = {
        schemaVersion: 1 as const,
        clientId: this.clientId,
        clientOperationId: operation,
        expectedRevision: row.revision,
        recordedOffline: typeof navigator !== 'undefined' && navigator.onLine === false,
        sourceRevisions: sources,
        occurredAt: new Date().toISOString(),
        effectiveAt: draft.reportDay,
      };
      const rejected = pendingCommand(row);
      await enqueueSiteCommand(
        this.store,
        this.scope,
        this.clientId,
        row.serverId ? 'action' : 'create',
        row.serverId
          ? { ...envelope, payload: action === 'save' ? { action, draft } : { action } }
          : { ...envelope, expectedRevision: 0, draft },
        row.localRevision,
        operation,
        Date.now(),
        rejected?.state === 'rejected' ? rejected.clientOperationId : undefined,
      );
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    }
  }
  sync() {
    return this.serial(() => this.syncNow());
  }
  private async syncNow() {
    await syncSiteOnce(this.store, this.scope, this.clientId, this.transport);
    this.row = await this.store.read(this.scope, this.clientId);
    return this.row!;
  }
  /** «Отправить, когда будет связь»: намерение хранится на устройстве вместе с черновиком. */
  setAutoSend(value: boolean) {
    return this.serial(async () => {
      await this.store.mutate(this.scope, this.clientId, (row) => {
        if (!row) throw new SiteOfflineError('not_found', 'Черновик не найден');
        row.autoSend = value;
        return { row, result: undefined };
      });
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  /**
   * Один шаг автоматической отправки при связи: фото → сохранение отчёта → отправка на проверку.
   * Каждый шаг ждёт подтверждения предыдущего; команда создаётся один раз с постоянным ключом.
   */
  advance(sources: DayPackage['sourceRevisions'], uploader: PhotoUploader) {
    return this.serial(async () => {
      const photos = await syncSitePhotos(this.store, this.scope, this.clientId, uploader, Date.now());
      this.row = await this.store.read(this.scope, this.clientId);
      let row = this.row;
      if (!row) throw new SiteOfflineError('not_found', 'Черновик не найден');
      if (!row.autoSend || row.authPaused || photos.stopped === 'refused') return { photos, row };
      if (photosPending(row.draft as SiteReportDraft, row.photos)) return { photos, row };
      const open = pendingCommand(row);
      if (open) {
        if (open.state === 'pending') row = await this.syncNow();
        else {
          // Отказ или встречная правка: автоотправка снимается, дальше решает человек
          await this.store.mutate(this.scope, this.clientId, (r) => {
            r!.autoSend = false;
            return { row: r!, result: undefined };
          });
          row = (await this.store.read(this.scope, this.clientId))!;
          this.row = row;
        }
        return { photos, row };
      }
      if (!editableReport(row.serverSnapshot as SiteReportSnapshot | null, this.scope.userId)) {
        await this.store.mutate(this.scope, this.clientId, (r) => {
          r!.autoSend = false;
          return { row: r!, result: undefined };
        });
        this.row = await this.store.read(this.scope, this.clientId);
        return { photos, row: this.row! };
      }
      const clean = row.serverId && row.syncedLocalRevision === row.localRevision;
      await this.commandNow(clean ? 'submit' : 'save', sources);
      if (clean)
        await this.store.mutate(this.scope, this.clientId, (r) => {
          r!.autoSend = false;
          return { row: r!, result: undefined };
        });
      row = await this.syncNow();
      return { photos, row };
    });
  }
  resume() {
    return this.serial(async () => {
      // Транспорт перед отправкой снова проверяет действительную сессию и её владельца.
      await resumeSiteSession(this.store, this.scope, this.clientId);
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
  resolve(chosen: SiteReportDraft, sources: DayPackage['sourceRevisions']) {
    return this.serial(async () => {
      const row = this.row!;
      const command = pendingCommand(row);
      if (!command) throw new Error('Нет конфликта для разрешения');
      await resolveSiteConflict(
        this.store,
        this.scope,
        this.clientId,
        command.clientOperationId,
        crypto.randomUUID(),
        chosen,
        sources,
        row.localRevision,
        Date.now(),
      );
      this.row = await this.store.read(this.scope, this.clientId);
      return this.row!;
    });
  }
}
